import './EntityDetailsDrawerContent.scss';

import { User } from 'mission-control-common-components';
import React from 'react';

import AdSideNavScheduling from '../../../../../common/AdSideNavScheduling';
import { NoteIcon_Dark, NoteIcon_Light, TargetingIcon_Dark, TargetingIcon_Light } from '../../../../../common/assets';
import Badge from '../../../../../common/Badge';
import ChangeLog from '../../../../../common/ChangeLog';
import IconButton from '../../../../../common/IconButton';
import { isRecognizedConfig } from '../../../../../configs';
import type { PermissionsNames } from '../../../../../constants';
import bem from '../../../../../utils/bem';
import AdListTab from '../../../../Ads/AdListTab/AdListTab';
import DrawerTargetingTable from '../../../DrawerTargetingTable';
import LineItemNotesTab from '../../../LineItemNotes/LineItemNotesTab';
import LineItemScheduling from '../../../LineItemScheduling';
import type { EntityDetailsDrawerData } from '../../../TraffickingPage/hooks/makePageDrawerPlugin';
import { EntityType } from '../../../TraffickingPage/hooks/makePageDrawerPlugin';

const [, drawerElement] = bem('trafficking-drawer-content');
const [menuItemBlock] = bem(drawerElement('menu-button')); // trafficking-drawer-content__menu-button

type Tabs = {
  tabTitle: string;
  navElement: JSX.Element;
  content: JSX.Element;
};

const buildTargetingTab = (data: EntityDetailsDrawerData, isExpanded: boolean): Tabs | null => {
  if (data?.dataType !== EntityType.LINE_ITEM && data?.dataType !== EntityType.AD) {
    return null;
  }

  const adEntityProps = {
    adType: data.dataType === EntityType.AD && data.type && isRecognizedConfig(data.type) ? data.type.key : null,
    ...(data.dataType === EntityType.AD && {
      adProduct: data.lineItem?.adProduct?.key,
      hasInvalidPublisher: data.hasInvalidPublisher,
      hasInvalidCountry: data.hasInvalidCountry,
    }),
  };

  return {
    tabTitle: 'Targeting',
    navElement: (
      <div role="listitem">
        <img src={TargetingIcon_Light} className={drawerElement('img-icon', 'selected')} alt="Targeting" />
        <img src={TargetingIcon_Dark} className={drawerElement('img-icon', 'unselected')} alt="Targeting" />
        {isExpanded && <span>Targeting</span>}
      </div>
    ),
    content: <DrawerTargetingTable id={data.id} entityType={data.dataType} {...adEntityProps} />,
  };
};

const buildLineItemSchedulingTab = (data: EntityDetailsDrawerData, isExpanded: boolean): Tabs | null => {
  if (data?.dataType !== EntityType.LINE_ITEM) {
    return null;
  }

  return {
    tabTitle: 'Line Item Scheduling',
    navElement: (
      <div role="listitem">
        <IconButton icon="IconCalendar" className={menuItemBlock('with-icon')} />
        {isExpanded && <span>Scheduling</span>}
      </div>
    ),
    content: <LineItemScheduling schedule={data.schedule} />,
  };
};

const buildAdSchedulingTab = (data: EntityDetailsDrawerData, isExpanded: boolean): Tabs | null => {
  if (data?.dataType !== EntityType.AD) {
    return null;
  }

  return {
    tabTitle: 'Ad Scheduling',
    navElement: (
      <div role="listitem">
        <IconButton icon="IconCalendar" className={menuItemBlock('with-icon')} />
        {isExpanded && <span>Scheduling</span>}
      </div>
    ),
    content: data.id ? (
      <AdSideNavScheduling
        adId={data.id}
        adSchedule={data?.schedule ?? null}
        lineItemSchedule={data?.lineItem?.schedule || null}
      />
    ) : (
      <div>No current schedule data for this {data.dataType}.</div>
    ),
  };
};

const buildAdListTab = (
  data: EntityDetailsDrawerData,
  isExpanded: boolean,
  hasAccessToReadAdList: boolean | undefined,
  drawerRef?: React.RefObject<HTMLElement>
): Tabs | null => {
  if (!hasAccessToReadAdList) {
    return null;
  }
  if (data?.dataType !== EntityType.LINE_ITEM && data?.dataType !== EntityType.AD) {
    return null;
  }

  const lineItemId = data?.dataType === EntityType.LINE_ITEM ? data.id : data.lineItem?.id;

  return {
    tabTitle: 'Ad List',
    navElement: (
      <div role="listitem">
        <Badge text={EntityType.AD} as="button" className={menuItemBlock()} />
        {isExpanded && <span>Ad List</span>}
      </div>
    ),
    content: lineItemId ? (
      <AdListTab lineItemId={lineItemId} scrollElementRef={drawerRef} />
    ) : (
      <div>No current ad list data for this {data.dataType}.</div>
    ),
  };
};

const buildLineItemNotesTab = (data: EntityDetailsDrawerData): Tabs | null => {
  if (data?.dataType !== EntityType.LINE_ITEM) {
    return null;
  }

  return {
    tabTitle: 'Line Item Notes',
    navElement: (
      <div role="listitem">
        <img src={NoteIcon_Light} className={drawerElement('img-icon', 'selected')} alt="Line item notes" />
        <img src={NoteIcon_Dark} className={drawerElement('img-icon', 'unselected')} alt="Line item notes" />
      </div>
    ),
    content: <LineItemNotesTab lineItemId={data.id} />,
  };
};

const buildChangeLogTab = (
  data: EntityDetailsDrawerData,
  isExpanded: boolean,
  hasAccessToReadChangeLog: boolean | undefined
): Tabs | null => {
  if (!hasAccessToReadChangeLog) {
    return null;
  }

  return {
    tabTitle: 'Change Log',
    navElement: (
      <div role="listitem">
        <IconButton icon="IconHistory" className={menuItemBlock('with-icon')} />
        {isExpanded && <span>Change log</span>}
      </div>
    ),
    content: <ChangeLog id={data?.id} entityType={data?.dataType} />,
  };
};

export const generateTraffickingTabs = (
  data: EntityDetailsDrawerData,
  isExpanded: boolean,
  drawerRef?: React.RefObject<HTMLElement>
): Tabs[] => {
  const permissions = User.getPermissions<PermissionsNames>();
  const hasAccessToReadAdList = permissions?.readRotation && permissions.readTraffickingAds;
  const hasAccessToReadChangeLog = permissions?.readTraffickingChangeLog;

  const candidateTabs: (Tabs | null)[] = [
    buildTargetingTab(data, isExpanded),
    buildLineItemSchedulingTab(data, isExpanded),
    buildAdSchedulingTab(data, isExpanded),
    buildAdListTab(data, isExpanded, hasAccessToReadAdList, drawerRef),
    buildLineItemNotesTab(data),
    buildChangeLogTab(data, isExpanded, hasAccessToReadChangeLog),
  ];

  return candidateTabs.filter((tab): tab is Tabs => tab !== null);
};
